import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Package, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ORDER_LABELS = {
  aliver_one_time: "One-time purchase · 60 patches",
  aliver_subscription: "Subscribe & Save · monthly",
};

export default function Account() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checked, setChecked] = useState(Boolean(location.state?.user));
  const [orders, setOrders] = useState([]);
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const submitEmailAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      if (mode === "signup") {
        await api.post("/auth/signup", { name, email, password });
      }
      const { data } = await api.post("/auth/login", { email, password });
      setUser({ ...data.user, user_id: data.user.user_id || data.user.id });
    } catch (err) {
      setAuthError(formatApiError(err));
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.user) return;
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecked(true));
  }, [location.state]);

  useEffect(() => {
    if (user) {
      api.get("/account/orders").then(({ data }) => setOrders(data.orders)).catch(() => {});
    }
  }, [user]);

  const signOut = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    navigate("/");
  };

  if (!checked) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center" data-testid="account-loading">
        <Loader2 className="animate-spin text-gold" size={32} strokeWidth={1.5} />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-5 py-14" data-testid="account-sign-in-page">
        <div className="w-full max-w-sm rounded-[2rem] border border-champagne bg-white p-9 shadow-[0_32px_80px_rgb(168,134,27,0.10)]">
          <div className="text-center">
            <span className="font-display text-2xl tracking-[0.15em] text-gold-deep">ALIVER</span>
            <h1 className="mt-4 font-display text-3xl text-ink" data-testid="account-auth-title">
              {mode === "signin" ? "Welcome back" : "Create your account"}
            </h1>
            <p className="mt-2 text-sm text-[#8C8C8C] leading-relaxed">
              Sign in to see your orders and manage your subscription.
            </p>
          </div>
          <form onSubmit={submitEmailAuth} className="mt-7 space-y-3" data-testid="email-auth-form">
            {mode === "signup" && (
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="account-name-input"
                className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
              />
            )}
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              data-testid="account-email-input"
              className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
            />
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
              data-testid="account-password-input"
              className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
            />
            {authError && (
              <p className="text-sm text-red-600" data-testid="account-auth-error">{authError}</p>
            )}
            <button
              type="submit"
              disabled={authLoading}
              data-testid="email-auth-submit-btn"
              className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-ink text-white font-semibold py-3.5 hover:bg-gold-deep transition-colors duration-300 disabled:opacity-70"
            >
              {authLoading ? <Loader2 size={16} className="animate-spin" /> : mode === "signin" ? "Sign In" : "Create Account"}
            </button>
          </form>
          <button
            type="button"
            onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setAuthError(""); }}
            data-testid="auth-mode-toggle"
            className="mt-4 w-full text-center text-sm text-[#8C8C8C] hover:text-ink transition-colors duration-300"
          >
            {mode === "signin" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
          <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[#8C8C8C]">
            <span className="flex-1 h-px bg-champagne" /> or <span className="flex-1 h-px bg-champagne" />
          </div>
          <button
            type="button"
            data-testid="google-sign-in-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/account";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white border border-champagne px-6 py-3.5 text-sm font-semibold text-ink hover:border-gold hover:shadow-[0_8px_24px_rgb(201,162,39,0.12)] transition-[border-color,box-shadow] duration-300"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"/>
              <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C6.68 5.16 7.66 3.58 9 3.58Z"/>
            </svg>
            Continue with Google
          </button>
          <div className="mt-5 text-center">
            <Link to="/" data-testid="account-back-home-link" className="text-sm text-[#8C8C8C] hover:text-ink transition-colors duration-300">
              Back to shop
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream" data-testid="account-page">
      <header className="border-b border-champagne/70 glass-nav sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="account-logo" className="font-display text-2xl tracking-[0.18em] text-gold-deep">ALIVER</Link>
          <button
            onClick={signOut}
            data-testid="account-sign-out-btn"
            className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-ink transition-colors duration-300"
          >
            <LogOut size={15} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border border-champagne" data-testid="account-avatar" />
          ) : (
            <span className="w-14 h-14 rounded-full bg-champagne flex items-center justify-center font-display text-2xl text-gold-deep" data-testid="account-avatar">
              {(user.name || user.email || "A").charAt(0).toUpperCase()}
            </span>
          )}
          <div>
            <h1 className="font-display text-3xl text-ink" data-testid="account-name">{user.name || "Welcome"}</h1>
            <p className="text-sm text-[#8C8C8C]" data-testid="account-email">{user.email}</p>
          </div>
        </div>

        <h2 className="mt-12 text-xs font-semibold tracking-[0.24em] uppercase text-[#8C8C8C]">Your orders</h2>
        <div className="mt-5 space-y-3" data-testid="account-orders">
          {orders.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gold-light bg-champagne/30 p-8 text-center" data-testid="account-orders-empty">
              <Package size={26} strokeWidth={1} className="mx-auto text-gold-deep" />
              <p className="mt-3 text-sm text-[#5C5C5C]">No orders yet on this email address.</p>
              <Link
                to="/#shop"
                data-testid="account-shop-link"
                className="mt-5 inline-flex rounded-full bg-ink text-white text-sm font-semibold px-6 py-3 hover:bg-gold-deep transition-colors duration-300"
              >
                Shop the gold eye masks — £19.99
              </Link>
            </div>
          )}
          {orders.map((o, i) => (
            <div key={o.session_id} className="rounded-2xl border border-champagne bg-white p-5 flex items-center justify-between gap-4" data-testid={`account-order-${i}`}>
              <div>
                <p className="font-semibold text-sm text-ink">{ORDER_LABELS[o.lookup_key] || o.lookup_key}</p>
                <p className="mt-1 text-xs text-[#8C8C8C]">
                  {o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : ""} · {o.payment_status}
                </p>
              </div>
              <span className="font-display text-2xl text-ink">£{Number(o.amount || 0).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-champagne bg-white p-6">
          <h3 className="font-display text-xl text-ink">Manage your subscription</h3>
          <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">
            To pause or cancel a Subscribe &amp; Save plan, email{" "}
            <a href="mailto:hello@aliver.co.uk" className="font-semibold text-ink underline decoration-gold-light underline-offset-2">hello@aliver.co.uk</a>{" "}
            from this address and we will take care of it, in line with our{" "}
            <Link to="/legal/subscription-terms" className="underline decoration-gold-light underline-offset-2">subscription terms</Link>.
          </p>
        </div>

        <Link to="/" data-testid="account-back-link" className="mt-10 inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-ink transition-colors duration-300">
          <ArrowLeft size={15} strokeWidth={1.5} /> Back to shop
        </Link>
      </div>
    </main>
  );
}
